import type { FC, ReactNode } from "react";
import { AlertCircle, CheckCircle, InfoCircle } from "@untitledui/icons";
import { cx } from "@/utils/cx";

/**
 * InlineAlert — inline notice s libovolným children obsahem (na rozdíl od
 * nativního AlertFloating/AlertFullWidth, který přijímá jen title+description).
 *
 * Color-not-only signaling (WCAG AA 1.4.1): pevná leading ikona
 * (InfoCircle / CheckCircle / AlertCircle) + barva, ne jenom barva.
 * ARIA: role="alert" + aria-live="assertive" pro error/warning,
 * role="status" + aria-live="polite" pro info/success.
 *
 * Dark mode jede automaticky přes UUI utility tokeny (bg-utility-*-50 atd.).
 */

export type InlineAlertVariant = "info" | "success" | "warning" | "error";

export interface InlineAlertProps {
    variant?: InlineAlertVariant;
    title?: string;
    children: ReactNode;
    icon?: FC<{ className?: string }>;
    className?: string;
    "data-testid"?: string;
}

const VARIANT_ICON: Record<InlineAlertVariant, FC<{ className?: string }>> = {
    info: InfoCircle,
    success: CheckCircle,
    warning: AlertCircle,
    error: AlertCircle,
};

const surfaceClasses: Record<InlineAlertVariant, string> = {
    info: "bg-utility-blue-50 ring-utility-blue-200 text-utility-blue-700",
    success: "bg-utility-green-50 ring-utility-green-200 text-utility-green-700",
    warning: "bg-utility-amber-50 ring-utility-amber-200 text-utility-amber-700",
    error: "bg-utility-red-50 ring-utility-red-200 text-utility-red-700",
};

const iconClasses: Record<InlineAlertVariant, string> = {
    info: "text-utility-blue-500",
    success: "text-utility-green-500",
    warning: "text-utility-amber-500",
    error: "text-utility-red-500",
};

export function InlineAlert({
    variant = "info",
    title,
    children,
    icon,
    className,
    "data-testid": testId,
}: InlineAlertProps) {
    const isAssertive = variant === "error" || variant === "warning";
    const Icon = icon ?? VARIANT_ICON[variant];
    return (
        <div
            data-variant={variant}
            role={isAssertive ? "alert" : "status"}
            aria-live={isAssertive ? "assertive" : "polite"}
            data-testid={testId}
            className={cx(
                "flex items-start gap-3 rounded-xl p-4 ring-1",
                surfaceClasses[variant],
                className,
            )}
        >
            <Icon aria-hidden="true" className={cx("mt-0.5 size-5 shrink-0", iconClasses[variant])} />
            <div className="min-w-0 flex-1">
                {title && <p className="mb-1 text-sm font-semibold">{title}</p>}
                <div className="text-sm">{children}</div>
            </div>
        </div>
    );
}
